import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import FilterListIcon from '@material-ui/icons/FilterList'
import { memo } from 'react'

import PageHeader from '../../components/PageHeader'
import PromotionTabs from '../../components/PromotionTabs'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import promotionRoleManagement from '../../staticRoleManagement/promotionRoleManagement'
import useStyles from './styles/PromotionsHeader.styles'

interface PromotionsHeaderProps {
  filterOpen: boolean
  toggleFilter: () => void
}

const PromotionsHeader: React.FC<PromotionsHeaderProps> = ({
  filterOpen,
  toggleFilter
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // UI ONLY. This hides a button; it authorizes nothing. The role string comes from browser
  // storage and the table is static, so anyone with devtools can flip it — or skip the UI and
  // call savePromotion directly. The server MUST independently enforce Promotion:Create, and
  // this is a money surface (a promotion discounts real bookings), so the cost of that check
  // being wrong is high.
  //
  // Recorded here because the two mutating entry points on this screen now gate DIFFERENTLY:
  // Delete uses `canWrite`, a per-record flag the server computed, while Create uses this
  // static role list. A maintainer answering a "why can't role X create?" complaint must not
  // "fix" it by widening the list on the assumption that this is the real gate.
  const hasAccessToCreatePromotion = promotionRoleManagement
    .find((item) => item.route === '/promotions/create')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  return (
    <div className={classes.header}>
      <PageHeader title={getTranslate('Promotions')} />
      <Typography variant="srOnly" component="h1">
        {getTranslate('Promotions')}
      </Typography>
      <PromotionTabs />
      <div className={classes.actionsRow}>
        <div className={classes.actions} data-cy="PromotionsHeaderActions">
          <Button
            startIcon={<FilterListIcon />}
            variant="contained"
            color="primary"
            data-cy="PromotionsFilterButton"
            aria-expanded={filterOpen}
            aria-controls="promotions-filter-panel"
            onClick={toggleFilter}>
            {getTranslate('Filter')}
          </Button>
          {hasAccessToCreatePromotion && (
            <Button
              startIcon={<AddIcon />}
              data-cy="AddPromotionButton"
              variant="contained"
              color="primary"
              component={LocalizedLink}
              to="/promotions/create">
              {getTranslate(isSmallScreen ? 'Add' : 'Add Promotion')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(PromotionsHeader)
